import React, { useMemo } from 'react';
import { ExtensionBasePrice } from 'common/types';
import { useSelector } from 'react-redux';
import { Box, Collapse, Switch, FormControlLabel } from '@mui/material';
import { getPricingString } from 'common/utils/common';
import Banner, { BannerProps } from 'components/Banner';

import MoneyTextField from 'common/components/MoneyTextField';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';

interface Props {
	value: ExtensionBasePrice;
	onChange: (value: ExtensionBasePrice) => void;
	disabled?: boolean;
}

const ExtensionPricing = (props: Props) => {
	const { value, onChange, disabled } = props;
	const { t } = useTranslation();
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const error: BannerProps | null = useMemo(() => {
		if (!value.pricePerHour && !value.pricePerDay) {
			return {
				variant: 'danger',
				title: t(
					'product.extensionPricing.pleaseEnterAtLeastOnePrice',
					'Please enter at least one of the above to enable extensions.',
				),
			};
		}

		if (!value.pricePerHour && value.pricePerDay) {
			return {
				variant: 'warning',
				title: t(
					'product.extensionPricing.hourlyExtensionsDisabled',
					'The minimum extension will be 24 hours. Enter an hourly price to enable shorter extensions.',
				),
			};
		}

		if (value.pricePerDay && value.pricePerHour) {
			if (value.pricePerHour * 24 < value.pricePerDay) {
				return {
					variant: 'warning',
					title: t('product.extensionPricing.dailyExtensionMoreThanHourly', {
						price: getPricingString(24 * value.pricePerHour, shopCurrency),
						defaultValue:
							'The price for an additional day is more than 24 times the hourly rate ({{price}}). The extension price will be calculated using the cheaper option.',
					}),
				};
			}

			if (value.pricePerDay < value.pricePerHour) {
				return {
					variant: 'warning',
					title: t(
						'product.extensionPricing.hourlyExtensionMoreThanDaily',
						'The price for an additional day is less than the price for an additional hour. Are you sure this is what you want?',
					),
				};
			}
		}
		return null;
	}, [value, shopCurrency, t]);

	const handleEnabledChange = (__: any, enabled: boolean) => {
		onChange({
			...value,
			enabled,
		});
	};

	const handlePricePerHourChange = (pricePerHour: number) => {
		onChange({
			...value,
			pricePerHour,
		});
	};

	const handlePricePerDayChange = (pricePerDay: number) => {
		onChange({
			...value,
			pricePerDay,
		});
	};

	return (
		<Box>
			<FormControlLabel
				control={
					<Switch
						checked={Boolean(value?.enabled)}
						onChange={handleEnabledChange}
						name="checkedB"
						color="primary"
					/>
				}
				label={t('product.extensionPricing.enableExtensions', 'Enable extensions for this product')}
				disabled={disabled}
			/>
			<Collapse in={value?.enabled}>
				<Box mt={4} maxWidth={400}>
					<MoneyTextField
						currency={shopCurrency}
						label={t('product.extensionPricing.pricePerHour', 'Price per additional hour')}
						value={value.pricePerHour}
						useMinorUnits
						onValueChange={handlePricePerHourChange}
						variant="outlined"
						fullWidth
						disabled={disabled}
					/>
				</Box>
				<Box mt={2} mb={2} maxWidth={400}>
					<MoneyTextField
						currency={shopCurrency}
						label={t('product.extensionPricing.pricePerDay', 'Price per additional day (24h)')}
						value={value.pricePerDay}
						useMinorUnits
						onValueChange={handlePricePerDayChange}
						variant="outlined"
						fullWidth
						disabled={disabled}
						helperText={
							value.pricePerHour
								? `24h x ${getPricingString(value.pricePerHour, shopCurrency)} = ${getPricingString(
										value.pricePerHour * 24,
										shopCurrency,
								  )}`
								: ''
						}
					/>
				</Box>
				{error && <Banner {...error} />}
			</Collapse>
		</Box>
	);
};

export default ExtensionPricing;
